import { defineStore } from 'pinia';
import { useQueryClient } from '@tanstack/vue-query';
import searchService from '../services/searchService';
import logger from '../utils/logger';
import { albumKeys, artistKeys, concertKeys, festivalKeys } from '../queries/queryKeys';
import { useSearchSSE } from '../composables/useSearchSSE';

let queryClientInstance = null;
const sse = useSearchSSE();

function getQueryClient() {
    if (!queryClientInstance) {
        try {
            queryClientInstance = useQueryClient();
        } catch {
            return null;
        }
    }
    return queryClientInstance;
}

export const useSearchStore = defineStore('search', {
    state: () => ({
        query: '',
        results: {
            artists: [],
            albums: [],
            concerts: [],
            festivals: [],
        },
        // For specific tab views where we might paginate
        detailedResults: {
            items: [],
            total: 0,
            hasMore: false,
            nextOffset: 0
        },
        loading: {
            all: false,
            artists: false,
            albums: false,
            concerts: false,
            festivals: false,
            searchView: false // General loading state for the main view
        },
        error: null,
        activeTab: 'all', // 'all', 'artists', 'albums', 'concerts', 'festivals'
        // Request deduplication - discard stale responses
        _currentSearchId: 0
    }),

    actions: {
        setQuery(query) {
            this.query = query;
        },

        setActiveTab(tab) {
            this.activeTab = tab;
        },

        /**
         * Cancel all active SSE streams
         */
        _cancelActiveStreams() {
            sse.cancelAllStreams();

            this.loading.albums = false;
            this.loading.concerts = false;
            this.loading.festivals = false;
            this._updateGlobalLoading();
        },

        _updateGlobalLoading() {
            this.loading.all = Boolean(
                this.loading.artists ||
                this.loading.albums ||
                this.loading.concerts ||
                this.loading.festivals
            )
        },

        _cancelSearchViewStream() {
            sse.cancelSearchViewStream();

            this.loading.searchView = false;
        },

        async performGlobalSearch(query, options = {}) {
            if (!query || query.trim().length < 2) {
                this.clearResults();
                return;
            }

            const { forceExternal = false } = options;

            // Cancel any pending streams from previous search
            this._cancelActiveStreams();

            // Increment search ID for request deduplication
            const searchId = ++this._currentSearchId;

            this.loading.all = true;
            this.loading.artists = true;
            this.loading.albums = true;
            this.loading.concerts = true;
            this.loading.festivals = true;
            this.error = null;
            this.query = query;

            // Clear previous results
            this.results = {
                artists: [],
                albums: [],
                concerts: [],
                festivals: [],
            };

            // Artists (Normal) - with deduplication check
            this._updateGlobalLoading();
            searchService.searchArtists(query, 5)
                .then(res => {
                    // Discard stale response
                    if (searchId !== this._currentSearchId) return;

                    const items = res.items || [];
                    const seenById = new Set();
                    const seenByName = new Set();
                    this.results.artists = items.filter(item => {
                        if (!item.id || seenById.has(item.id)) return false;
                        seenById.add(item.id);
                        const nameKey = (item.name || '').toLowerCase().trim();
                        if (nameKey && seenByName.has(nameKey)) return false;
                        if (nameKey) seenByName.add(nameKey);
                        return true;
                    });
                })
                .catch(e => {
                    if (searchId === this._currentSearchId) {
                        logger.error('Artists search error', e);
                    }
                })
                .finally(() => {
                    if (searchId !== this._currentSearchId) return;
                    this.loading.artists = false;
                    this._updateGlobalLoading();
                });

            // Albums (SSE)
            sse.startStream('albums', () => searchService.searchAlbums(query, 5, 0, false, {
                onData: (s, d) => this._handleStreamData('albums', d),
                onError: (e) => {
                    logger.error('Albums SSE error', e);
                    this.loading.albums = false;
                    this._updateGlobalLoading();
                    sse.clearStream('albums');
                },
                onDone: () => {
                    this.loading.albums = false;
                    this._updateGlobalLoading();
                    sse.clearStream('albums');
                }
            }));

            // Concerts (SSE)
            sse.startStream('concerts', () => searchService.searchConcerts(query, 5, 0, {
                streamCallbacks: {
                    onData: (s, d) => this._handleStreamData('concerts', d),
                    onError: (e) => {
                        logger.error('Concerts SSE error', e);
                        this.loading.concerts = false;
                        this._updateGlobalLoading();
                        sse.clearStream('concerts');
                    },
                    onDone: () => {
                        this.loading.concerts = false;
                        this._updateGlobalLoading();
                        sse.clearStream('concerts');
                    }
                },
                forceExternal
            }));

            // Festivals (SSE)
            sse.startStream('festivals', () => searchService.searchFestivals(query, 5, 0, {
                streamCallbacks: {
                    onData: (s, d) => this._handleStreamData('festivals', d),
                    onError: (e) => {
                        logger.error('Festivals SSE error', e);
                        this.loading.festivals = false;
                        this._updateGlobalLoading();
                        sse.clearStream('festivals');
                    },
                    onDone: () => {
                        this.loading.festivals = false;
                        this._updateGlobalLoading();
                        sse.clearStream('festivals');
                    }
                },
                forceExternal
            }));

            // Allow UI to render while background requests run; keep aggregate loading accurate
            this._updateGlobalLoading();
        },

        /**
         * Perform a scoped search (e.g., only artists) but still populate the 'results' object
         * so it displays in the "All" tab view format.
         */
        async performScopedSearch(query, type, options = {}) {
            if (!query || !type || query.trim().length < 2) {
                this.clearResults();
                return;
            }

            const { forceExternal = false } = options;

            this._cancelActiveStreams();
            this._cancelSearchViewStream();

            // Increment search ID for request deduplication
            const searchId = ++this._currentSearchId;

            this.loading.all = true;
            this.error = null;
            this.query = query;

            // Reset all results first
            this.results = {
                artists: [],
                albums: [],
                concerts: [],
                festivals: [],
            };

            try {
                let response;
                let items = [];

                switch (type) {
                    case 'artists':
                        response = await searchService.searchArtists(query, 20);
                        items = response?.items || [];
                        break;
                    case 'albums':
                        response = await searchService.searchAlbums(query, 20);
                        items = response?.items || [];
                        break;
                    case 'concerts':
                        response = await searchService.searchConcerts(query, 20, 0, { forceExternal });
                        items = response?.items || [];
                        break;
                    case 'festivals':
                        response = await searchService.searchFestivals(query, 20, 0, { forceExternal });
                        items = response?.items || [];
                        break;
                }

                // Discard stale response
                if (searchId !== this._currentSearchId) return;

                // Deduplicate items by ID and name (case-insensitive)
                const seenById = new Set();
                const seenByName = new Set();
                const uniqueItems = items.filter(item => {
                    if (!item.id || seenById.has(item.id)) return false;
                    seenById.add(item.id);
                    const nameKey = (item.name || '').toLowerCase().trim();
                    if (nameKey && seenByName.has(nameKey)) return false;
                    if (nameKey) seenByName.add(nameKey);
                    return true;
                });

                this.results[type] = uniqueItems;

            } catch (err) {
                if (searchId !== this._currentSearchId) return;
                logger.error(`Scoped search for ${type} failed:`, err);
                this.error = `Failed to search ${type}. Please try again.`;
            } finally {
                if (searchId === this._currentSearchId) {
                    this.loading.all = false;
                }
            }
        },

        async searchByType(type, query, limit = 20, offset = 0, options = {}) {
            if (!query || query.trim().length < 2) {
                this.clearResults();
                return;
            }

            const { forceExternal = false } = options;

            this.loading.searchView = true;
            this.error = null;

            if (offset === 0) {
                this.detailedResults = {
                    items: [],
                    total: 0,
                    hasMore: false,
                    nextOffset: 0
                };
            }

            this._cancelSearchViewStream();

            // Increment search ID for request deduplication
            const searchId = ++this._currentSearchId;

            try {
                const onData = (source, data) => {
                    // Discard stale response
                    if (searchId !== this._currentSearchId) return;

                    if (!data || !data.items) return;
                    // Deduplicate
                    const newItems = Array.isArray(data.items) ? data.items : [];
                    const currentItems = Array.isArray(this.detailedResults.items) ? this.detailedResults.items : [];
                    const existingIds = new Set(currentItems.map(i => i.id));
                    const uniqueNew = newItems.filter(i => !existingIds.has(i.id));
                    this.detailedResults.items = [...currentItems, ...uniqueNew];

                    const total = data.total_count || data.total || data.meta?.total
                    if (total !== undefined) this.detailedResults.total = total;
                    
                    const backendHasMore = data.has_more !== undefined ? data.has_more : (data.hasMore !== undefined ? data.hasMore : data.meta?.hasMore);
                    if (backendHasMore !== undefined) {
                        this.detailedResults.hasMore = backendHasMore;
                    } else if (total !== undefined) {
                        this.detailedResults.hasMore = this.detailedResults.items.length < total;
                    }
                    
                    const nextOffset = data.next_offset || data.nextOffset || (data.items ? offset + data.items.length : undefined);
                    if (nextOffset !== undefined) this.detailedResults.nextOffset = nextOffset;
                };

                let response;
                switch (type) {
                    case 'artists': {
                        // Artist search uses TanStack Query cache when available
                        response = await searchService.searchArtists(query, limit, offset);

                        // Discard stale response
                        if (searchId !== this._currentSearchId) return;

                        const items = response.items || [];
                        if (offset === 0) {
                            // Deduplicate initial results by ID and name
                            const seenById = new Set();
                            const seenByName = new Set();
                            this.detailedResults.items = items.filter(item => {
                                if (!item.id || seenById.has(item.id)) return false;
                                seenById.add(item.id);
                                const nameKey = (item.name || '').toLowerCase().trim();
                                if (nameKey && seenByName.has(nameKey)) return false;
                                if (nameKey) seenByName.add(nameKey);
                                return true;
                            });
                        } else {
                            // Append and deduplicate against current
                            const existingIds = new Set(this.detailedResults.items.map(i => i.id));
                            const existingNames = new Set(this.detailedResults.items.map(i => (i.name || '').toLowerCase().trim()));
                            const uniqueNew = items.filter(i => {
                                if (existingIds.has(i.id)) return false;
                                const nameKey = (i.name || '').toLowerCase().trim();
                                if (nameKey && existingNames.has(nameKey)) return false;
                                return true;
                            });
                            this.detailedResults.items = [...this.detailedResults.items, ...uniqueNew];
                        }
                        this.detailedResults.total = response.total_count || response.total || response.meta?.total || 0;
                        this.detailedResults.hasMore = response.has_more !== undefined ? response.has_more : (response.hasMore !== undefined ? response.hasMore : (response.meta?.hasMore || (this.detailedResults.items.length < this.detailedResults.total)));
                        this.detailedResults.nextOffset = response.next_offset || response.nextOffset || offset + limit;
                        this.loading.searchView = false;
                        break;
                    }
                    case 'albums':
                        sse.startSearchViewStream(() => searchService.searchAlbums(query, limit, offset, false, {
                            onData,
                            onError: (err) => {
                                logger.error('Albums SSE error', err);
                                this.error = 'Error loading albums';
                                this.loading.searchView = false;
                                sse.clearSearchViewStream();
                            },
                            onDone: () => {
                                this.loading.searchView = false;
                                sse.clearSearchViewStream();
                            }
                        }));
                        break;
                    case 'concerts':
                        sse.startSearchViewStream(() => searchService.searchConcerts(query, limit, offset, {
                            streamCallbacks: {
                                onData,
                                onError: (err) => {
                                    logger.error('Concerts SSE error', err);
                                    this.error = 'Error loading concerts';
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                },
                                onDone: () => {
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                }
                            },
                            forceExternal
                        }));
                        break;
                    case 'festivals':
                        sse.startSearchViewStream(() => searchService.searchFestivals(query, limit, offset, {
                            streamCallbacks: {
                                onData: (s, d) => onData(s, d),
                                onError: (err) => {
                                    if (searchId !== this._currentSearchId) return;
                                    logger.error('Festivals SSE error', err);
                                    this.error = 'Error loading festivals';
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                },
                                onDone: () => {
                                    if (searchId !== this._currentSearchId) return;
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                }
                            },
                            forceExternal
                        }));
                        break;
                }
            } catch (err) {
                if (searchId !== this._currentSearchId) return;
                logger.error(`Search failed for ${type}:`, err);
                this.error = `Failed to load ${type}.`;
                this.loading.searchView = false;
                sse.clearSearchViewStream();
            }
        },

        async searchAdvancedAlbums({ artist, title }, limit = 20, offset = 0) {
            this.loading.searchView = true;
            this.error = null;

            this._cancelSearchViewStream();

            // Increment search ID for request deduplication
            const searchId = ++this._currentSearchId;

            try {
                let response;
                if (artist && title) {
                    response = await searchService.searchAlbumsByBoth(artist, title, limit, offset);
                } else if (artist) {
                    response = await searchService.searchAlbumsByArtist(artist, limit, offset);
                } else if (title) {
                    response = await searchService.searchAlbumsByTitle(title, limit, offset);
                } else {
                    return;
                }

                // Discard stale response
                if (searchId !== this._currentSearchId) return;

                const items = response.items || response.results?.items || [];
                
                // Deduplicate
                const seen = new Set();
                if (offset === 0) {
                    this.detailedResults.items = items.filter(item => {
                        if (!item.id || seen.has(item.id)) return false;
                        seen.add(item.id);
                        return true;
                    });
                } else {
                    const existingIds = new Set(this.detailedResults.items.map(i => i.id));
                    const uniqueNew = items.filter(i => !existingIds.has(i.id));
                    this.detailedResults.items = [...this.detailedResults.items, ...uniqueNew];
                }

                this.detailedResults.total = response.total_count || 0;
                this.detailedResults.hasMore = response.has_more || (this.detailedResults.items.length < this.detailedResults.total);
                this.detailedResults.nextOffset = offset + limit;

            } catch (err) {
                if (searchId !== this._currentSearchId) return;
                logger.error('Advanced album search failed:', err);
                this.error = 'Failed to perform advanced album search.';
            } finally {
                if (searchId === this._currentSearchId) {
                    this.loading.searchView = false;
                }
            }
        },

        clearResults() {
            this._cancelActiveStreams();
            this._cancelSearchViewStream();
            this.loading = {
                all: false,
                artists: false,
                albums: false,
                concerts: false,
                festivals: false,
                searchView: false
            };
            this.results = {
                artists: [],
                albums: [],
                concerts: [],
                festivals: [],
            };
            this.detailedResults = {
                items: [],
                total: 0,
                hasMore: false,
                nextOffset: 0
            };
            this.query = '';
            this.error = null;
        },

        // Helper action for global search results
        _handleStreamData(type, data) {
            if (!data || (!data.items && !data.results)) return;

            const items = data.items || data.results || [];
            // Deduplicate against existing global results for this type
            const currentItems = Array.isArray(this.results[type]) ? this.results[type] : [];
            const currentIds = new Set(currentItems.map(i => i.id));
            const newItems = items.filter(i => !currentIds.has(i.id));

            this.results[type] = [...currentItems, ...newItems];
        },

        /**
         * Invalidate search-related caches in TanStack Query
         * Call this after operations that might affect search results
         */
        invalidateSearchCache() {
            const qc = getQueryClient();
            if (!qc) return;
            
            qc.invalidateQueries({ queryKey: albumKeys.all });
            qc.invalidateQueries({ queryKey: artistKeys.all });
            qc.invalidateQueries({ queryKey: concertKeys.all });
            qc.invalidateQueries({ queryKey: festivalKeys.all });
        },

        /**
         * Search with filters - supports advanced filtering options
         */
        async searchWithFilters(type, query, filters = {}, options = {}) {
            if (!query || query.trim().length < 2) {
                this.clearResults();
                return;
            }

            const {
                limit = 20,
                offset = 0,
                forceExternal = false
            } = { ...filters, ...options };

            this.loading.searchView = true;
            this.error = null;

            if (offset === 0) {
                this.detailedResults = {
                    items: [],
                    total: 0,
                    hasMore: false,
                    nextOffset: 0
                };
            }

            this._cancelSearchViewStream();

            // Increment search ID for request deduplication
            const searchId = ++this._currentSearchId;

            try {
                const onData = (source, data) => {
                    // Discard stale response
                    if (searchId !== this._currentSearchId) return;

                    if (!data || !data.items) return;
                    const newItems = Array.isArray(data.items) ? data.items : [];
                    const currentItems = Array.isArray(this.detailedResults.items) ? this.detailedResults.items : [];
                    const existingIds = new Set(currentItems.map(i => i.id));
                    const uniqueNew = newItems.filter(i => !existingIds.has(i.id));
                    this.detailedResults.items = [...currentItems, ...uniqueNew];

                    const total = data.total_count || data.total || data.meta?.total
                    if (total !== undefined) this.detailedResults.total = total;
                    
                    const backendHasMore = data.has_more !== undefined ? data.has_more : (data.hasMore !== undefined ? data.hasMore : data.meta?.hasMore);
                    if (backendHasMore !== undefined) {
                        this.detailedResults.hasMore = backendHasMore;
                    } else if (total !== undefined) {
                        this.detailedResults.hasMore = this.detailedResults.items.length < total;
                    }
                    
                    const nextOffset = data.next_offset || data.nextOffset || (data.items ? offset + data.items.length : undefined);
                    if (nextOffset !== undefined) this.detailedResults.nextOffset = nextOffset;
                };

                switch (type) {
                    case 'albums':
                        sse.startSearchViewStream(() => searchService.searchAlbums(query, limit, offset, false, {
                            onData,
                            onError: (err) => {
                                if (searchId !== this._currentSearchId) return;
                                logger.error('Filtered albums SSE error', err);
                                this.error = 'Error loading albums';
                                this.loading.searchView = false;
                                sse.clearSearchViewStream();
                            },
                            onDone: () => {
                                if (searchId !== this._currentSearchId) return;
                                this.loading.searchView = false;
                                sse.clearSearchViewStream();
                            }
                        }));
                        break;
                    case 'concerts':
                        sse.startSearchViewStream(() => searchService.searchConcerts(query, limit, offset, {
                            streamCallbacks: {
                                onData,
                                onError: (err) => {
                                    if (searchId !== this._currentSearchId) return;
                                    logger.error('Filtered concerts SSE error', err);
                                    this.error = 'Error loading concerts';
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                },
                                onDone: () => {
                                    if (searchId !== this._currentSearchId) return;
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                }
                            },
                            forceExternal
                        }));
                        break;
                    case 'festivals':
                        sse.startSearchViewStream(() => searchService.searchFestivals(query, limit, offset, {
                            streamCallbacks: {
                                onData: (s, d) => onData(s, d),
                                onError: (err) => {
                                    if (searchId !== this._currentSearchId) return;
                                    logger.error('Filtered festivals SSE error', err);
                                    this.error = 'Error loading festivals';
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                },
                                onDone: () => {
                                    if (searchId !== this._currentSearchId) return;
                                    this.loading.searchView = false;
                                    sse.clearSearchViewStream();
                                }
                            },
                            forceExternal
                        }));
                        break;
                    default:
                        if (searchId === this._currentSearchId) {
                            this.loading.searchView = false;
                        }
                        logger.warn('Unknown search type:', type);
                }
            } catch (err) {
                if (searchId !== this._currentSearchId) return;
                logger.error(`Filtered search failed for ${type}:`, err);
                this.error = `Failed to load ${type}.`;
                this.loading.searchView = false;
                sse.clearSearchViewStream();
            }
        },

        /**
         * Load more results for paginated search
         */
        async loadMore(type) {
            if (!this.detailedResults.hasMore || this.loading.searchView) {
                return;
            }

            const nextOffset = this.detailedResults.nextOffset;
            await this.searchByType(type, this.query, 20, nextOffset);
        },

        /**
         * Reset all search state
         */
        reset() {
            this._cancelActiveStreams();
            this._cancelSearchViewStream();
            this.$reset();
        }
    }
});
